import { useState } from "react";
import { trpcClient } from "@/lib/trpc";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { QCFinalForm } from "@/pages/quality/qc-final/QCFinalForm";

export interface QCFinalInspectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productionOrderId?: string;
  initialData?: any;
  onSuccess?: (result: { id: string; finalQcId?: string; record?: any }) => void;
}

/**
 * Lập / sửa biên bản KCS thành phẩm (bộ phận QC). Lô Đạt / Nhân nhượng tự bàn giao cho Sản xuất (Phiếu đóng gói);
 * in tem thùng / pallet và nhập kho làm ở Xưởng sản xuất › Đóng gói & Nhập kho.
 */
export function QCFinalInspectionModal({ open, onOpenChange, productionOrderId, initialData, onSuccess }: QCFinalInspectionModalProps) {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (formData: any) => {
    setIsSubmitting(true);
    try {
      const payload = { ...formData, aqlStandard: Number(formData.aqlStandard) || 2.5 };
      let result: any;
      if (initialData?.id) {
        result = await trpcClient.qcFinal.update.mutate({ id: initialData.id, data: payload });
        toast.success("Cập nhật biên bản nghiệm thu FQC thành công!");
      } else {
        result = await trpcClient.qcFinal.create.mutate(payload);
        const accepted = result?.status === "PASSED" || result?.status === "WARNING";
        toast.success(
          `Nghiệm thu FQC ${result?.finalQcId || ""} thành công!${accepted ? " Lô đã bàn giao cho Sản xuất đóng gói." : ""}`,
          { duration: 5000 },
        );
      }

      await queryClient.invalidateQueries({ queryKey: ["qcFinal"] });
      await queryClient.invalidateQueries({ queryKey: ["po-fqc-list"] });
      await queryClient.invalidateQueries({ queryKey: ["productionOrder"] });
      await queryClient.invalidateQueries({ queryKey: ["packingSlips"] });

      if (onSuccess && result) onSuccess({ id: result.id, finalQcId: result.finalQcId, record: result });
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err?.message || "Lỗi khi lưu biên bản FQC");
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <QCFinalForm
      open={open}
      onOpenChange={onOpenChange}
      onSubmit={handleSubmit}
      initialData={initialData}
      params={{ productionOrderId }}
      isSubmitting={isSubmitting}
      onCancel={() => onOpenChange(false)}
    />
  );
}

export default QCFinalInspectionModal;
